import React, { useState, useEffect, useRef, useCallback } from 'react';
import PropTypes from 'prop-types';
import Thumbnail from './Thumbnail';
import './ThumbnailGrid.css';

const ThumbnailGrid = ({
  thumbnailSize,
  categoryStates,
  tokenCategoryStates,
  raritySortOrder,
  rarityFilter,
  subcategoryFilter,
  tokenNumberFilter,
  tokenNumberSortOrder,
  attributeCountFilter,
  allData,
  paginationMode,
}) => {
  const [filteredData, setFilteredData] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const observer = useRef();
  const itemsPerPage = 20;

  const lastCardRef = useCallback(
    (node) => {
      if (loading || paginationMode === 'manual') return;
      if (observer.current) observer.current.disconnect();
      observer.current = new IntersectionObserver((entries) => {
        if (entries[0].isIntersecting) {
          setCurrentPage((prevPage) => prevPage + 1);
        }
      });
      if (node) observer.current.observe(node);
    },
    [loading, paginationMode]
  );

  useEffect(() => {
    setCurrentPage(1);
  }, [categoryStates, tokenCategoryStates, raritySortOrder, rarityFilter, subcategoryFilter, tokenNumberFilter, tokenNumberSortOrder, attributeCountFilter]);

  useEffect(() => {
    setLoading(true);
    let filtered = [...allData];

    if (tokenNumberFilter) {
      filtered = filtered.filter((item) => item.tokenID === parseInt(tokenNumberFilter, 10));
    }

    if (rarityFilter) {
      const [min, max] = rarityFilter.split('_').map(Number);
      filtered = filtered.filter((item) => item.rarity_rank >= min && item.rarity_rank <= max);
    }

    if (attributeCountFilter.length > 0) {
      filtered = filtered.filter((item) => attributeCountFilter.includes(item.attributes.length.toString()));
    }

    if (subcategoryFilter.length > 0) {
      filtered = filtered.filter((item) =>
        subcategoryFilter.every(([title, option]) => {
          const normalizedOption = option ? option.replace(/_/g, ' ') : '';
          return item.attributes.some(attr =>
            attr.trait_type.toLowerCase() === title.toLowerCase() &&
            (normalizedOption ? attr.value.toLowerCase() === normalizedOption.toLowerCase() : true)
          );
        })
      );
    }

    if (raritySortOrder === 'ascending_rarity') {
      filtered.sort((a, b) => a.rarity_rank - b.rarity_rank);
    } else if (raritySortOrder === 'descending_rarity') {
      filtered.sort((a, b) => b.rarity_rank - a.rarity_rank);
    }

    if (tokenNumberSortOrder === 'ascending') {
      filtered.sort((a, b) => a.tokenID - b.tokenID);
    } else if (tokenNumberSortOrder === 'descending') {
      filtered.sort((a, b) => b.tokenID - a.tokenID);
    }

    const startIndex = paginationMode === 'manual' ? (currentPage - 1) * itemsPerPage : 0;
    const endIndex = paginationMode === 'manual' ? currentPage * itemsPerPage : currentPage * itemsPerPage;
    setFilteredData(filtered.slice(startIndex, endIndex));
    setLoading(false);
  }, [categoryStates, tokenCategoryStates, raritySortOrder, rarityFilter, subcategoryFilter, tokenNumberFilter, tokenNumberSortOrder, attributeCountFilter, currentPage, allData, paginationMode]);

  const totalPages = Math.ceil(allData.length / itemsPerPage);

  const handlePrevious = () => {
    if (currentPage > 1) {
      setCurrentPage(currentPage - 1);
    }
  };

  const handleNext = () => {
    if (currentPage < totalPages) {
      setCurrentPage(currentPage + 1);
    }
  };

  const handlePageClick = (page) => {
    setCurrentPage(page);
  };

  const renderPagination = () => {
    if (paginationMode !== 'manual') return null;

    const pageButtonStyle = {
      margin: '0 5px',
      padding: '5px 10px',
      backgroundColor: 'transparent',
      color: '#fff',
      border: '1px solid #fff',
      borderRadius: '3px',
      cursor: 'pointer'
    };

    const activePageButtonStyle = {
      ...pageButtonStyle,
      backgroundColor: '#32efa5',
      color: '#000'
    };

    const disabledButtonStyle = {
      ...pageButtonStyle,
      opacity: 0.5,
      cursor: 'not-allowed'
    };

    const ellipsisStyle = {
      margin: '0 5px',
      color: '#fff'
    };

    const renderPageNumbers = () => {
      const pages = [];
      
      // First page
      pages.push(
        <button
          key={1}
          onClick={() => handlePageClick(1)}
          style={currentPage === 1 ? activePageButtonStyle : pageButtonStyle}
        >
          1
        </button>
      );

      // Left ellipsis
      if (currentPage > 3) {
        pages.push(<span key="left-ellipsis" style={ellipsisStyle}>...</span>);
      }

      // Nearby pages
      const startPage = Math.max(2, currentPage - 1);
      const endPage = Math.min(totalPages - 1, currentPage + 1);
      for (let i = startPage; i <= endPage; i++) {
        pages.push(
          <button
            key={i}
            onClick={() => handlePageClick(i)}
            style={currentPage === i ? activePageButtonStyle : pageButtonStyle}
          >
            {i}
          </button>
        );
      }

      // Right ellipsis
      if (currentPage < totalPages - 2) {
        pages.push(<span key="right-ellipsis" style={ellipsisStyle}>...</span>);
      }

      // Last page
      if (totalPages > 1) {
        pages.push(
          <button
            key={totalPages}
            onClick={() => handlePageClick(totalPages)}
            style={currentPage === totalPages ? activePageButtonStyle : pageButtonStyle}
          >
            {totalPages}
          </button>
        );
      }

      return pages;
    };

    return (
      <div style={{ margin: '20px 0', textAlign: 'center' }}>
        <button
          onClick={handlePrevious}
          disabled={currentPage === 1}
          style={currentPage === 1 ? disabledButtonStyle : pageButtonStyle}
        >
          Previous
        </button>
        {renderPageNumbers()}
        <button
          onClick={handleNext}
          disabled={currentPage === totalPages}
          style={currentPage === totalPages ? disabledButtonStyle : pageButtonStyle}
        >
          Next
        </button>
      </div>
    );
  };
  console.log('debug->filteredData', filteredData.length)
  return (
    <div className="thumbnail-grid-container">
      {filteredData.length >= 5 ? renderPagination() : <div style={{ margin: '20px 0', textAlign: 'center' }}></div> }
      <div className="thumbnail-grid">
        {filteredData.map((item, index) => {
          const imageUrl = `/token/${item.tokenID}.thumb.jpg`;
          if (paginationMode === 'automatic' && filteredData.length === index + 1) {
            return (
              <div ref={lastCardRef} key={index}>
                <Thumbnail
                  tokenID={item.tokenID}
                  tokenName={item.name}
                  traits={item.attributes}
                  rarityRank={item.rarity_rank}
                  thumbnailSize={thumbnailSize}
                  imageUrl={imageUrl}
                />
              </div>
            );
          } else {
            return (
              <Thumbnail
                key={index}
                tokenID={item.tokenID}
                tokenName={item.name}
                traits={item.attributes}
                rarityRank={item.rarity_rank}
                thumbnailSize={thumbnailSize}
                imageUrl={imageUrl}
              />
            );
          }
        })}
        {loading && <div>Loading...</div>}
      </div>
      {renderPagination()}
    </div>
  );
};

ThumbnailGrid.propTypes = {
  thumbnailSize: PropTypes.string.isRequired,
  categoryStates: PropTypes.object.isRequired,
  tokenCategoryStates: PropTypes.object.isRequired,
  raritySortOrder: PropTypes.string,
  rarityFilter: PropTypes.string,
  subcategoryFilter: PropTypes.array,
  tokenNumberFilter: PropTypes.string,
  tokenNumberSortOrder: PropTypes.string,
  attributeCountFilter: PropTypes.array,
  allData: PropTypes.array.isRequired,
  paginationMode: PropTypes.string.isRequired,
};

export default ThumbnailGrid;
